import { type FC } from 'react'

import { useTranslation } from 'react-i18next'
import './_addPoll.scss'
import TextareaAutosize from 'react-textarea-autosize'

interface Props {
    title: string
    options: {
        a: string
        b: string
        c?: string
        d?: string
    }
    handleOptionChange: (key: string, value: string) => void
    setPollTitle: (value: string) => void
    handleAddOption: () => void
    validationError?: string
    handleRemoveOption: (key: 'c' | 'd') => void
}

const POLL_QUESTION_MAX_LENGTH = 50
const POLL_ANSWER_MAX_LENGTH = 25
const POLL_ALLOWED_CHARACTERS_REGEX = /^[A-Za-z0-9 ?!,.:;)]*$/

const Poll: FC<Props> = ({
    title,
    options,
    handleAddOption,
    setPollTitle,
    handleOptionChange,
    validationError,
    handleRemoveOption
}) => {
    const { t } = useTranslation()

    const handlePollTitleChange = (value: string) => {
        if (POLL_ALLOWED_CHARACTERS_REGEX.test(value)) {
            setPollTitle(value)
        }
    }

    const handlePollOptionChange = (key: string, value: string) => {
        if (POLL_ALLOWED_CHARACTERS_REGEX.test(value)) {
            handleOptionChange(key, value)
        }
    }

    return (
        <div className='poll'>
            <div className='poll__header'>
                <TextareaAutosize
                    name='pollTitle'
                    id='pollTitle'
                    placeholder={`${t('pollName')}...`}
                    maxLength={POLL_QUESTION_MAX_LENGTH}
                    value={title}
                    minRows={1}
                    maxRows={2}
                    onChange={e => {
                        handlePollTitleChange(e.target.value)
                    }}
                />
            </div>

            <div className='poll__content'>
                <div
                    className={`poll__option poll__option--${
                        !options.a.length ? 'empty' : ''
                    }`}
                >
                    <div className='poll__option__icon'>{t('a')}</div>
                    <div className='poll__option__input'>
                        <input
                            type='text'
                            placeholder={t('option1')}
                            maxLength={POLL_ANSWER_MAX_LENGTH}
                            value={options.a}
                            onChange={e => {
                                handlePollOptionChange('a', e.target.value)
                            }}
                        />
                    </div>
                </div>

                <div
                    className={`poll__option poll__option--${
                        !options.b.length ? 'empty' : ''
                    }`}
                >
                    <div className='poll__option__icon'>{t('b')}</div>
                    <div className='poll__option__input'>
                        <input
                            type='text'
                            placeholder={t('option2')}
                            maxLength={POLL_ANSWER_MAX_LENGTH}
                            value={options.b}
                            onChange={e => {
                                handlePollOptionChange('b', e.target.value)
                            }}
                        />
                    </div>
                </div>

                {Object.prototype.hasOwnProperty.call(options, 'c') && (
                    <div
                        className={`poll__option poll__option--${
                            !options.c?.length ? 'empty' : ''
                        }`}
                    >
                        <div className='poll__option__icon'>{t('c')}</div>

                        <div className='poll__option__input'>
                            <input
                                type='text'
                                placeholder={t('option3')}
                                maxLength={POLL_ANSWER_MAX_LENGTH}
                                value={options.c}
                                onChange={e => {
                                    handlePollOptionChange(
                                        'c',
                                        e.target.value
                                    )
                                }}
                            />
                        </div>

                        <button
                            type='button'
                            className='poll__option__remove'
                            onClick={e => {
                                e.preventDefault()
                                e.stopPropagation()
                                handleRemoveOption('c')
                            }}
                        >
                            ✕
                        </button>
                    </div>
                )}

                {Object.prototype.hasOwnProperty.call(options, 'd') && (
                    <div
                        className={`poll__option poll__option--${
                            !options.d?.length ? 'empty' : ''
                        }`}
                    >
                        <div className='poll__option__icon'>{t('d')}</div>

                        <div className='poll__option__input'>
                            <input
                                type='text'
                                placeholder={t('option4')}
                                maxLength={POLL_ANSWER_MAX_LENGTH}
                                value={options.d}
                                onChange={e => {
                                    handlePollOptionChange(
                                        'd',
                                        e.target.value
                                    )
                                }}
                            />
                        </div>

                        <button
                            type='button'
                            className='poll__option__remove'
                            onClick={e => {
                                e.preventDefault()
                                e.stopPropagation()
                                handleRemoveOption('d')
                            }}
                        >
                            ✕
                        </button>
                    </div>
                )}

                {!Object.prototype.hasOwnProperty.call(options, 'd') && (
                    <div
                        className='poll__option poll__option--add'
                        onClick={handleAddOption}
                    >
                        <div className='poll__option__icon'>+</div>
                        <div className='poll__option__text'>{t('add')}</div>
                    </div>
                )}

                {validationError && (
                    <div className='poll__content__error'>
                        {validationError}
                    </div>
                )}
            </div>
        </div>
    )
}

export default Poll